import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { BottomButtonsLayout, Button, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import HslContext from 'context/HslContext';
import ServiceContext from 'context/ServiceContext';
import { format, isAfter } from 'date-fns';
import { CREATE_HSL_TICKET } from 'graphql/mutations/createHslTicket';
import { READ_HSL_TICKETS } from 'graphql/queries/readHslTickets';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUniqueIdSync } from 'react-native-device-info';
import { useAuthStore } from 'stores/authStore';
import { useHslStore } from 'stores/hslStore';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import {
  AccountType,
  HslZone,
  Mutation,
  MutationCreateHslTicketArgs,
  TicketType,
} from 'types/generatedSchemaTypes';
import { HslParkingStackParamList, ServicesNavProps } from 'types/navigation';
import { getLocale } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { titleTypeText } from 'utils/hslUtils';
import { Container } from '../styles/styles';
import HslPricingView from './HslPricingView';
import ValueSelectionItem from './ValueSelectionItem';
import { useUiStore } from 'stores/uiStore';

interface IconProps {
  name: string;
  color: string;
}

export const BackComponent: React.FC<IconProps> = () => (
  <Icon name="back" color={theme.colors.white} />
);

const BuyTicket: React.FC = () => {
  const {
    selectedSubscriptionType,
    selectedTicketType,
    selections,
    hslPricingResult,
    hslTicketPolicy,
    setCurrentScreen,
  } = useContext(HslContext);
  const { customer } = useContext(AccountContext);
  const { t } = useTranslation();
  const { selectedZone, validFrom, customerType } = selections;
  const selectedPricing = useHslStore((state) => state.selectedHslPricing);
  const {
    customer: { countryCode },
  } = useContext(AccountContext);
  const { myHslTickets } = useContext(ServiceContext);
  const isUserRestricted = useAuthStore((state) => state.accountRestricted);

  const { goBack, canGoBack } =
    useNavigation<StackNavigationProp<HslParkingStackParamList>>();
  const { navigate } = useNavigation<ServicesNavProps>();

  const handleBack = () => {
    if (canGoBack()) {
      goBack();
    } else {
      navigate('hslNavigator', { screen: 'hslTicket' });
    }
  };
  const tabBarHeight = useBottomMenuHeight();

  const phoneNumberValue = !customer?.mobilePhone
    ? t('hslTicket.mandatory')
    : customer.mobilePhone;
  const phoneNumberColor = !customer?.mobilePhone
    ? theme.colors.aimoUiRed
    : theme.colors.gray300;

  const paymentMethod = useSelectedPaymentMethod();

  const [createHslTicket, { loading }] = useMutation<
    Mutation,
    MutationCreateHslTicketArgs
  >(CREATE_HSL_TICKET, {
    refetchQueries: [{ query: READ_HSL_TICKETS }],
    onCompleted: (data) => {
      const formatForDate =
        selectedTicketType?.type === 'SINGLE' ? 'd.M.Y HH:mm' : 'd.M.Y';
      const date = format(
        new Date(data.createHslTicket?.hslDetails?.validTo as Date),
        `${formatForDate}`,
        { locale: getLocale() }
      );
      const text = t('success.hslTicketPurchase', {
        type: titleTypeText(data.createHslTicket?.hslDetails?.ticketType, true),
        date: date,
      });
      showToast(`${text}`, 'success');
    },
    onError: () => showToast('error.hslTicketCreationFailed', 'error'),
  });

  useEffect(() => {
    if (
      hslPricingResult?.data?.readHslTicketPricing &&
      hslPricingResult?.data?.readHslTicketPricing.length > 0
    ) {
      if (selectedSubscriptionType === 'AUTOSAVER') {
        useHslStore
          .getState()
          .setSelectedHslPricing(
            hslPricingResult?.data?.readHslTicketPricing.find(
              (pricing) => pricing?.validityPeriod === 30
            ) ?? null
          );
        return;
      }
      const val =
        selectedTicketType?.type !== 'SINGLE'
          ? hslPricingResult?.data?.readHslTicketPricing.find(
              (pricing) =>
                pricing?.validityPeriod === selectedPricing?.validityPeriod
            ) || hslPricingResult?.data?.readHslTicketPricing?.[0]
          : hslPricingResult?.data?.readHslTicketPricing?.[0];
      useHslStore.getState().setSelectedHslPricing(val ?? null);
    }
  }, [
    hslPricingResult,
    selectedPricing,
    selectedTicketType,
    selectedSubscriptionType,
  ]);

  const formatDate =
    validFrom === 'Now'
      ? validFrom
      : format(validFrom as Date, 'd.M.Y HH:mm', { locale: getLocale() });

  const isLoading = hslPricingResult?.loading;

  const validityPeriod = () => {
    if (selectedPricing) {
      return selectedTicketType?.type === 'SINGLE'
        ? t('hslTicket.validity.minutes', {
            value: selectedPricing.validityMinutes,
          })
        : t('hslTicket.validity.days', {
            value: selectedPricing.validityPeriod,
          });
    }
    return '';
  };

  const onPress = async () => {
    if (isUserRestricted) {
      return useUiStore.getState().setSheetModal('accountRestricted');
    } else if (hasMaxActiveTickets()) {
      return showToast(
        t('hslTicket.maxTicketsReached', {
          type: t(
            `hslTicket.types.${selectedTicketType?.type?.toLowerCase()}.title`
          ).toLowerCase(),
        }),
        'error'
      );
    }
    await createHslTicket({
      variables: {
        input: {
          phoneNumber: customer.mobilePhone as string,
          ticketType: selectedTicketType?.type as TicketType,
          accountType: customerType?.key as AccountType,
          zones: selectedZone as HslZone,
          creditCardId: paymentMethod?.cardId as string,
          autoRenewable: selectedSubscriptionType === 'AUTOSAVER',
          validityPeriod:
            selectedTicketType?.type === 'SINGLE'
              ? null
              : selectedPricing?.validityPeriod,
          locale: countryCode ?? 'FI',
          deviceId: isWeb ? customer.contactEmail : getUniqueIdSync(),
          validFrom: validFrom === 'Now' ? null : (validFrom as string),
        },
      },
    });
    setCurrentScreen('');
    handleBack();
  };

  const hasMaxActiveTickets = () => {
    if (!hslTicketPolicy || !selectedTicketType?.type) {
      return false;
    }
    switch (selectedTicketType.type as TicketType) {
      case 'SEASON':
        return (
          currentTicketAmount('SEASON') >=
          hslTicketPolicy.maxActiveSeasonTickets
        );
      case 'DAY':
        return (
          currentTicketAmount('DAY') >= hslTicketPolicy.maxActiveDayTickets
        );
      case 'SINGLE':
        return (
          currentTicketAmount('SINGLE') >=
          hslTicketPolicy.maxActiveSingleTickets
        );
      default:
        return false;
    }
  };

  const currentTicketAmount = (ticketType: TicketType) => {
    return myHslTickets.filter(
      (ticket) =>
        ticket.hslDetails.ticketType === ticketType &&
        ticket.hslDetails.validTo &&
        isAfter(new Date(ticket.hslDetails.validTo), new Date())
    ).length;
  };

  const isSelectedZoneEmpty = selectedZone === '';
  const isCustomerTypeKeyEmpty = customerType?.key === '';
  const disabled = loading ?? isSelectedZoneEmpty ?? isCustomerTypeKeyEmpty;

  return (
    <BottomButtonsLayout
      contentBottom={
        <StyledWrapper center maxWidth={300} mb={tabBarHeight - 50}>
          <Button
            text={t('hslTicket.buyTicket')}
            buttonType="secondary"
            size="l"
            testID="hsl-buy-ticket-button"
            disabled={disabled}
            onPress={onPress}
            loading={loading}
          />
        </StyledWrapper>
      }>
      <Container testID="hsl-buy-ticket-container">
        <ColumnWithMargins mt={45} ml={20} mr={20}>
          <ValueSelectionItem
            title={t('hslTicket.zoneTitle')}
            value={selectedZone}
            selectable
            testId="hsl-purchase-select-zone"
            onPress={() =>
              navigate('hslNavigator', { screen: 'hslZoneSelect' })
            }
          />
          {selectedTicketType?.type === 'SEASON' && (
            <ValueSelectionItem
              title={t('hslTicket.subscriptionTitle')}
              value={t(
                `hslTicket.subscriptionTypes.${selectedSubscriptionType.toLowerCase()}.selected`
              )}
              selectable
              loading={isLoading}
              testId="hsl-purchase-select-subscription"
              onPress={() =>
                navigate('hslNavigator', { screen: 'hslSubscriptionSelect' })
              }
            />
          )}
          <ValueSelectionItem
            title={t('hslTicket.durationTitle')}
            value={validityPeriod()}
            selectable={
              selectedTicketType?.type !== 'SINGLE' &&
              selectedSubscriptionType !== 'AUTOSAVER'
            }
            loading={isLoading}
            testId="hsl-purchase-select-duration"
            onPress={() =>
              navigate('hslNavigator', { screen: 'hslDurationSelect' })
            }
          />
          <ValueSelectionItem
            title={t('hslTicket.validFromTitle')}
            value={formatDate}
            selectable
            testId="hsl-purchase-select-validFrom"
            onPress={() =>
              navigate('hslNavigator', { screen: 'hslValidFromSelect' })
            }
          />
          <ValueSelectionItem
            title={t('hslTicket.customerTypeTitle')}
            value={customerType?.value}
            selectable={false}
            noBorder
            testId="hsl-purchase-select-customerType"
            onPress={() => null}
          />
          <ValueSelectionItem
            title={t('hslTicket.phoneNumber')}
            value={phoneNumberValue}
            valueColor={phoneNumberColor}
            selectable
            testId="hsl-purchase-select-validFrom"
            onPress={() =>
              navigate('hslNavigator', { screen: 'hslUpdatePhone' })
            }
          />
          <HslPricingView />
        </ColumnWithMargins>
      </Container>
    </BottomButtonsLayout>
  );
};

export default BuyTicket;
